import jwtDecode from 'jwt-decode'

import {
	topInformationGenerator,
	topInformationTypes,
	closeTopInformation,
} from 'redux/reducers/helpers/topInformationGenerator'
import { localize } from 'helpers/translator'

import actionTypes from 'redux/actions/actionTypes'
import activityTypes from 'helpers/enums/activityTypes'
import draftStateTypes from 'helpers/enums/interactiveVideo/draftStateTypes'
import sessionTypes from 'helpers/enums/sessionTypes'
import userRoles from 'helpers/enums/userRoles'
import breakpointReachedOptions from 'helpers/enums/interactiveVideo/breakpointReachedOptions'
import videoStateTypes from 'helpers/enums/videoStateTypes'
import confirmTypes from 'helpers/enums/confirmTypes'
import payloadTypes from 'helpers/enums/interactiveVideo/payloadTypes'
import playbackSpeedTypes from 'helpers/enums/interactiveVideo/playbackSpeedTypes'
import {
	findActivityIndexByUid,
	getActivityByQuestionUid,
	getActivityPayloadKey,
	getActivityPayload,
	updateActivitySharingOptions,
} from './helpers/interactiveVideo/activities'
import {
	createShareSolution,
} from './helpers/interactiveVideo/mcqSharing'
import {
	createShareParticipantSubmission,
} from './helpers/interactiveVideo/oeqSharing'
import {
	addSharingOptionToSharingOptions,
	removeSharingOptionFromSharingOptions,
} from './helpers/interactiveVideo/sharingOptions'
import { VideoStreamModeTypes } from 'helpers/videoStreamMode'
import { defaultDraftByActivityType } from './helpers/interactiveVideo/drafts'
import volumeValues from 'helpers/enums/interactiveVideo/volumeValues'
import featureFlagsTypes from 'helpers/enums/featureFlagsTypes'
import activityStateTypes from 'helpers/enums/activities/activityStateTypes'
import slideStateTypes from 'helpers/enums/interactiveVideo/slideStateTypes'
import { getFlag } from 'helpers/featureFlags/provider'
import { isIphone } from 'helpers/userAgent'
import isActivityResponseEmpty from 'helpers/interactive_video/isActivityResponseEmpty'

const defaultCurrentActivity = {
	type: activityTypes.NONE_QUESTION,
}

const defaultBreakpointActivityUid = ''

const defaultState = {
	uid: '',
	media: {
		asset: {},
		breakpoints: [],
		duration: 0,
		trimStart: 0,
		trimEnd: 0,
	},
	currentActivity: defaultCurrentActivity,
	slideState: videoStateTypes.VIDEO_STATE_PAUSED,
	settings: {
		isInteractiveMode: false,
		shouldHideStudentNames: true,
		streamMode: VideoStreamModeTypes.ALL_DEVICES,
		selectedVideoQuality: 'medium',
		selectedVideoPlaybackSpeed: playbackSpeedTypes.NORMAL,
		volume: volumeValues.MAX_VOLUME,
		isMuted: false,
		captions: [],
		selectedLanguage: '',
	},
	isActivityPanelExpanded: true,
	isReport: false,
	role: userRoles.STUDENT,
	participants: [],
	confirms: [],
	activities: [],
	participantUid: '',
	sessionType: sessionTypes.UNSET,
	drafts: {},
	breakpointReachedData: {
		isApplySelected: false,
		selectedAction: breakpointReachedOptions.WAIT,
	},
	topInformation: [],
	handouts: [],
	canParticipate: true,
	activityState: activityStateTypes.STARTED,
	presentationSlideState: slideStateTypes.STARTED,
	participantState: {
		maximumTimeReached: 0,
		currentBreakpointActivityUid: '',
	},
}

function canExpandActivityPanel() {
	return !isIphone()
}

function initializeInteractiveVideo(state, action) {
	const decodedJwt = getDecodedJwt(action.jwt)
	return {
		...state,
		uid: action.uid,
		pm: decodedJwt.extra.pm,
		isHomeworkResults: decodedJwt.extra.homeworkResults === '1',
		isStudentResubmissionEnabled: decodedJwt.extra.studentResubmission === '1',
		isSubmissionRequiredEnabled: decodedJwt.extra.submissionRequired === '1',
		nickname: decodedJwt.extra.nickname,
		role: decodedJwt.isTeacher === '1' ?
			userRoles.TEACHER :
			userRoles.STUDENT,
		isActivityPanelExpanded: decodedJwt.isTeacher === '1' && canExpandActivityPanel(),
		//Needed to convert this for Student Paced, its string in the JWT
		sessionType: parseInt(decodedJwt.extra.session_type),
		isReport: decodedJwt.extra.is_report === true,
		isGuest: !!decodedJwt.extra.isGuest,
		isExternalImmersiveReaderEnabled: decodedJwt.extra.imrd,
		externalUid: decodedJwt.extra.custom_slide_id,
		sessionUid: decodedJwt.extra.session_uid,
		presentationSlideState: slideStateTypes.STARTED,
	}
}

function getDecodedJwt(jwt) {
	let decodedJwt
	try {
		decodedJwt = jwtDecode(jwt)
	} catch (err) {
		decodedJwt = {
			uid: '1',
			isTeacher: '0',
			sessionType: sessionTypes.LIVE_SESSION,
			extra: {
				nickname: '',
				session_type: '0',
				custom_slide_id: '0',
				is_report: false,
				is_preview: false,
			},
		}
	}
	return decodedJwt
}

function settingsInteractiveModeToggle(state) {
	const isInteractiveMode = !state.settings.isInteractiveMode
	const isActivityPanelExpanded =
		!isInteractiveMode ? false : state.isActivityPanelExpanded
	const currentActivity = getDefaultCurrentActivity()
	const participantState = setDefaultCurrentActivityForParticipant(state)
	return {
		...state,
		settings: {
			...state.settings,
			isInteractiveMode,
		},
		currentActivity,
		participantState,
		isActivityPanelExpanded,
	}
}

function updateSessionData(state, action) {
	let slideState = state.slideState
	const isStudent = state.role === userRoles.STUDENT
	if (state.currentActivity.type === activityTypes.MEDIA
		&& action.payload.currentActivity.type === activityTypes.MEDIA
		&& state.currentActivity.mediaPayload.range[0] !== action.payload.currentActivity.mediaPayload.range[0]
		&& isStudent
	) {
		slideState = videoStateTypes.VIDEO_STATE_PAUSED
	}
	const activities = mergeAllActivities(state.activities, action.payload.activities)
	const currentActivity = mergeCurrentActivity(action.payload.currentActivity, activities)
	const drafts = initializeDrafts(state, currentActivity, activities, isStudent, state.participantUid)
	const participants = action.payload.participants
	return {
		...state,
		currentActivity,
		media: {
			...state.media,
			breakpoints: action.payload.mediaData.breakpoints,
			asset: action.payload.mediaData.asset,
			uid: action.payload.mediaData.mediaUid,
			posterUid: action.payload.mediaData.posterUid,
			duration: action.payload.mediaData.duration,
			trimStart: action.payload.mediaData.trimStart,
			trimEnd: action.payload.mediaData.trimEnd,
		},
		isActivityPanelExpanded:
			[activityTypes.NONE_QUESTION, activityTypes.MEDIA].includes(action.payload.currentActivity.type)
			&& state.role === userRoles.TEACHER
				?	false
				:	state.isActivityPanelExpanded,
		settings: {
			...state.settings,
			isInteractiveMode: action.payload.settings.isInteractiveMode,
			shouldHideStudentNames:
				action.payload.settings.shouldHideStudentNames,
			streamMode:
				action.payload.settings.streamMode,
		},
		participants,
		activities,
		slideState,
		drafts,
		sessionType: selectSessionTypeByStreamMode(
			state.sessionType,
			state.settings.streamMode,
			action.payload.settings.streamMode,
		),
	}
}

function selectSessionTypeByStreamMode(oldSessionType, previosStreamMode, newStreamMode) {
	let newSessionType = oldSessionType
	if (previosStreamMode === VideoStreamModeTypes.STUDENT_PACED
		&& newStreamMode === VideoStreamModeTypes.TEACHER_DEVICE
	) {
		newSessionType = sessionTypes.LIVE_SESSION
	} else if (newStreamMode === VideoStreamModeTypes.STUDENT_PACED) {
		newSessionType = sessionTypes.HOMEWORK
	}
	return newSessionType
}

function mergeAllActivities(oldActivities, newActivities) {
	return newActivities.map(
		newActivity => {
			const payloadKey = payloadTypes[newActivity.type]
			const newActivityPayload = getActivityPayload(newActivity)
			const oldActivity = getActivityByQuestionUid(oldActivities, newActivityPayload.uid)
			const oldActivityPayload = oldActivity && getActivityPayload(oldActivity)
			const oldActivitySubmissions = oldActivityPayload && oldActivityPayload.submissions
			return {
				...newActivity,
				[payloadKey]: {
					...newActivityPayload,
					submissions: mergeActivitySubmissions(
						oldActivitySubmissions,
						newActivityPayload.submissions,
					),
				},
			}
		}
	)
}

function getNewDraftState(oldDraft, submission, activityType) {
	const newState = submission
		? (!oldDraft ? draftStateTypes.ENABLED : oldDraft.state)
		: (oldDraft ? oldDraft.state : defaultDraftByActivityType[activityType].state)
	return newState
}

function getNewDraftValue(oldDraft, submission, activityType) {
	let newValue
	if (submission) {
		const submissionValue = activityType === activityTypes.OPEN_ENDED_QUESTION
			? submission.value
			: submission.answerUids
		newValue = oldDraft
			? oldDraft.state === draftStateTypes.ENABLED
				? submissionValue
				: oldDraft.value
			: submissionValue
	} else {
		newValue = oldDraft ? oldDraft.value : defaultDraftByActivityType[activityType].value
	}

	return newValue
}

function mergeCurrentActivity(newCurrentActivity, newActivities) {
	let payload
	const allowedActivityTypes = [
		activityTypes.OPEN_ENDED_QUESTION,
		activityTypes.POLL_QUESTION,
		activityTypes.MULTIPLE_CHOICE_QUESTION,
	]
	if (allowedActivityTypes.includes(newCurrentActivity.type)) {
		const payloadKey = payloadTypes[newCurrentActivity.type]
		const newActivityPayload = getActivityPayload(newCurrentActivity)
		const oldActivity = getActivityByQuestionUid(newActivities, newActivityPayload.uid)
		const oldActivityPayload = getActivityPayload(oldActivity)
		const oldActivitySubmissions = oldActivityPayload && oldActivityPayload.submissions
		const newActivitySubmissions = newActivityPayload.submissions
		const submissions = mergeActivitySubmissions(oldActivitySubmissions, newActivitySubmissions)
		payload = {
			[payloadKey]: {
				...newActivityPayload,
				submissions,
			},
		}
	}
	return {
		...newCurrentActivity,
		...payload,
	}
}

function mergeActivitySubmissions(oldActivitySubmissions, newActivitySubmissions) {
	let oldSubmissions = oldActivitySubmissions || []
	const newSubmissions = newActivitySubmissions || []
	oldSubmissions = oldSubmissions.filter( oldSubmission => !newSubmissions.find(
		newSubmission => newSubmission.participantUid === oldSubmission.participantUid
	))
	return [
		...newSubmissions,
		...oldSubmissions,
	]
}

function initializeDrafts(state, newCurrentActivity, activities, isStudent, participantUid) {
	function requiresDraft(activity) {
		const activityTypesEnabledForDrafts = [
			activityTypes.OPEN_ENDED_QUESTION,
			activityTypes.POLL_QUESTION,
			activityTypes.MULTIPLE_CHOICE_QUESTION,
		]
		return activityTypesEnabledForDrafts.includes(activity.type)
	}

	function shouldEnableActivityDraft(previousCurrentActivityUid, newCurrentActivityUid, activityUid) {
		let hasActivityChanged = false
		if (newCurrentActivityUid !== '') {
			hasActivityChanged = previousCurrentActivityUid !== ''
				? newCurrentActivityUid !== previousCurrentActivityUid
				: true
		}
		return getFlag(featureFlagsTypes.INTERACTIVE_VIDEO_AUTOSUBMIT) //TO REMOVE
			&& (activityUid === newCurrentActivityUid)
			&& hasActivityChanged
	}

	let newDrafts = {}
	if (isStudent) {
		const oldDrafts = state.drafts
		const newCurrentActivityUid = requiresDraft(newCurrentActivity)
			? getActivityPayload(newCurrentActivity).uid
			: ''
		const previousCurrentActivityUid = requiresDraft(state.currentActivity)
			? getActivityPayload(state.currentActivity).uid
			: ''
		newDrafts = activities.reduce(
			(drafts, activity) => {
				if (requiresDraft(activity)) {
					const activityPayload = getActivityPayload(activity)
					const oldDraft = oldDrafts[activityPayload.uid]
					const submission = activityPayload.submissions.find(
						sub => sub.participantUid === participantUid
					)
					const shouldEnable = shouldEnableActivityDraft(
						previousCurrentActivityUid,
						newCurrentActivityUid,
						activityPayload.uid
					)
					const state = getNewDraftState(oldDraft, submission, activity.type)
					const value = getNewDraftValue(oldDraft, submission, activity.type)
					const newDraft = {
						state,
						value,
						isEditing: shouldEnable,
					}
					drafts[activityPayload.uid] = newDraft
				}
				return drafts
			}, {}
		)
	}
	return newDrafts
}

function initializeParticipantSubmissions(state, action) {
	const oeqSubmissionsDrafts = action.oeqSubmissions.reduce(
		(drafts, submission) => {
			const newDraft = {
				state: draftStateTypes.SUBMITTED,
				value: submission.value,
				isEditing: false,
			}
			drafts[submission.questionUid] = newDraft
			return drafts
		}, {}
	)
	const mcqSubmissionsDrafts = action.mcqSubmissions.reduce(
		(drafts, submission) => {
			const newDraft = {
				state: draftStateTypes.SUBMITTED,
				value: submission.answerUids,
				isEditing: false,
			}
			drafts[submission.questionUid] = newDraft
			return drafts
		}, {}
	)
	const newDrafts = {
		...oeqSubmissionsDrafts,
		...mcqSubmissionsDrafts,
	}
	const newState = Object.keys(newDrafts).reduce(
		(newState, questionUid) => {
			const activity = getActivityByQuestionUid(state.activities, questionUid)
			const updatedActivity = updateActivitySubmissions(
				activity,
				newState.participantUid,
				newDrafts[questionUid].value,
			)
			if (
				state.sessionType !== sessionTypes.HOMEWORK &&
					isCurrentActivity(newState.currentActivity, updatedActivity)
			) {
				newState = {
					...newState,
					currentActivity: updatedActivity,
				}
			}
			return {
				...newState,
				activities: updateActivity(updatedActivity, newState.activities),
			}
		}, state
	)

	return {
		...newState,
		drafts: {
			...state.drafts,
			...newDrafts,
		},
	}
}

function getPlayStateCurrentActivity(currentActivity) {
	if ([activityTypes.NONE_QUESTION, activityTypes.MEDIA].includes(currentActivity.type)) {
		return currentActivity
	} else {
		return getDefaultCurrentActivity()
	}
}

function setSlideState(state, newSlideState) {
	let participantState = state.participantState
	let currentActivity = state.currentActivity
	let isActivityPanelExpanded = state.isActivityPanelExpanded
	if (newSlideState === videoStateTypes.VIDEO_STATE_SEEKING) {
		participantState = setDefaultCurrentActivityForParticipant(state)
		currentActivity = getPlayStateCurrentActivity(currentActivity)
		isActivityPanelExpanded = false
	}
	return {
		...state,
		previousState: state.slideState,
		slideState: newSlideState,
		participantState,
		currentActivity,
		isActivityPanelExpanded,
	}
}

function shouldShowPreventSkippingPrompt(state, participant) {
	if (getFlag(featureFlagsTypes.INTERACTIVE_VIDEO_PREVENT_SKIPPING_VIDEOS)
		&& state.sessionType === sessionTypes.HOMEWORK
		&& state.role === userRoles.STUDENT
		&& participant
		&& state.participantState.currentBreakpointActivityUid !== ''
		&& state.isSubmissionRequiredEnabled
	) {
		const breakpointActivityUid = state.participantState.currentBreakpointActivityUid
		const currentActivity = getCurrentActivityByBreakpointActivityUid(state, breakpointActivityUid)
		const currentActivityPayload = getActivityPayload(currentActivity)
		const currentDraft = state.drafts[currentActivityPayload.uid]
		return isActivityResponseEmpty(currentActivity.type, currentDraft)
	} else {
		return false
	}
}

function handlePlaySelected(state) {
	const participant = state.participants.find(
		participant => participant.uid === state.participantUid
	)
	const isShowingPreventSkippingPrompt = shouldShowPreventSkippingPrompt(state, participant)
	if (isShowingPreventSkippingPrompt) {
		return openConfirm(state,
			{
				type: confirmTypes.INTERACTIVE_VIDEO_PREVENT_SKIPPING_PROMPT,
			}
		)
	}
	const newState = {
		...setSlideState(state, videoStateTypes.VIDEO_STATE_PLAYING),
		currentActivity: getPlayStateCurrentActivity(state.currentActivity),
		participantState: setDefaultCurrentActivityForParticipant(state),
	}
	return collapseActivityPanel(newState)
}

function setMaximumTimeReachedForParticipant(state, action) {
	// We only write greater values than the state stored
	if (action.value > state.participantState.maximumTimeReached) {
		return {
			...state,
			participantState: {
				...state.participantState,
				maximumTimeReached: action.value,
			},
		}
	}
	return state
}

function handleEndOfVideoReached(state) {
	return {
		...state,
		participantState: {
			...state.participantState,
			maximumTimeReached: state.media.trimEnd,
		},
	}
}

function setCurrentActivityForParticipant(state, breakpointActivityUid) {
	return {
		...state.participantState,
		currentBreakpointActivityUid: breakpointActivityUid,
	}
}

function getCurrentActivityByBreakpointActivityUid(
	state,
	breakpointActivityUid
) {
	if (!breakpointActivityUid)
		return getDefaultCurrentActivity()
	const breakpoints = state.media.breakpoints
	const activities = state.activities
	const breakpointsActivities = getBreakpointsActivities(breakpoints)
	const breakpointActivity = findBreakpointActivityByUid(
		breakpointsActivities,
		breakpointActivityUid
	)
	const currentActivity = findActivityByUid(
		activities,
		breakpointActivity.activityUid
	)
	return currentActivity || getDefaultCurrentActivity()
}

function setDefaultCurrentActivityForParticipant(state) {
	return setCurrentActivityForParticipant(state, defaultBreakpointActivityUid)
}

function getDefaultCurrentActivity() {
	return defaultCurrentActivity
}

function handleSeekEnded(state) {
	const newSlideState =
		state.previousState !== videoStateTypes.VIDEO_STATE_PLAYING
			? videoStateTypes.VIDEO_STATE_PAUSED
			: videoStateTypes.VIDEO_STATE_PLAYING
	return setSlideState(state, newSlideState)
}

function handleBreakpointActivitySelected(state, action) {
	const breakpointActivityUid = action.breakpointActivityUid
	const newState = setSlideState(
		state,
		videoStateTypes.VIDEO_STATE_BREAKPOINT_SELECTED,
	)
	const participantState = setCurrentActivityForParticipant(
		newState,
		breakpointActivityUid
	)
	const currentActivity = getCurrentActivityByBreakpointActivityUid(
		state,
		breakpointActivityUid
	)
	state = expandActivityPanelBySessionType(
		{
			...newState,
			participantState,
			currentActivity,
		}
	)
	// Only student, for SP: on activity change
	// Change to enable for edition, set the activity draft state to enabled
	if (
		getFlag(featureFlagsTypes.INTERACTIVE_VIDEO_AUTOSUBMIT)
		&& state.role === userRoles.STUDENT
	) {
		const currentActivityPayload = getActivityPayload(currentActivity)
		state = enableActivityDraftForEdition(state, currentActivityPayload.uid)
	}

	return closeConfirm(
		state, confirmTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACCESS,
	)
}

function handleBreakpointActivityConfirmed(state, action) {
	state = handleBreakpointActivitySelected(state, action)
	if (action.shouldRememberOption) {
		state = {
			...state,
			breakpointReachedData: {
				...state.breakpointReachedData,
				isApplySelected: true,
				selectedAction: breakpointReachedOptions.START_ACTIVITY,
			},
		}
	}
	return state
}

function handleBreakpointActivityReached(state, action) {
	let newState = state
	if (newState.currentActivity.type === activityTypes.MEDIA
		&& newState.sessionType === sessionTypes.LIVE_SESSION) {
		if (!newState.breakpointReachedData.isApplySelected) {
			newState = setSlideState(newState, videoStateTypes.VIDEO_STATE_PAUSED)
			newState = openConfirm(newState,
				{
					type: confirmTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACCESS,
					props: {
						breakpointUid: action.breakpointActivityUid,
					},
				})

		} else if (newState.breakpointReachedData.selectedAction === breakpointReachedOptions.START_ACTIVITY) {
			newState = handleBreakpointActivitySelected(state, action)
		} else {
			newState = setSlideState(newState, videoStateTypes.VIDEO_STATE_PAUSED)
		}
	} else {
		newState = handleBreakpointActivitySelected(state, action)
	}
	return newState
}

function handleBreakpointActivityDismissed(state, action) {
	let newState = closeConfirm(
		state, confirmTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACCESS,
	)
	if (action.shouldRememberOption) {
		newState = {
			...newState,
			breakpointReachedData: {
				...newState.breakpointReachedData,
				isApplySelected: true,
				selectedAction: breakpointReachedOptions.WAIT,
			},
		}
	}
	return newState
}

function activityPanelCollapseToggle(state) {
	return {
		...state,
		isActivityPanelExpanded: !state.isActivityPanelExpanded,
	}
}

function collapseActivityPanel(state) {
	return {
		...state,
		isActivityPanelExpanded: false,
	}
}

function expandActivityPanelBySessionType(state) {
	// If the session type is Live(unless its an iphone device),
	// the panel should be expanded when the activity starts
	// If the session type is FOC or StudentPaced,
	// the panel should be collapsed when the activity starts
	return {
		...state,
		isActivityPanelExpanded:
			(
				(
					state.sessionType === sessionTypes.LIVE_SESSION
					|| state.sessionType === sessionTypes.HOMEWORK
				)
				&& canExpandActivityPanel()
			) || state.isReport,
	}
}

function participantJoinedSession(state, action) {
	// Everytime the websockets get reconnected, this action will trigger again
	if (state.participantUid) {
		return state
	}
	const newState = {
		...state,
		participantUid: action.participantUid,
	}
	if (
		state.role === userRoles.TEACHER
		&& state.sessionType === sessionTypes.LIVE_SESSION
		&& !state.isReport
		&& getFlag(featureFlagsTypes.INTERACTIVE_VIDEO_LIVE_STUDENT_PACED_MODE)
	) {
		return openConfirm(newState,
			{
				type: confirmTypes.INTERACTIVE_VIDEO_STREAM_MODE_SELECT,
				props: {},
			}
		)
	}
	return newState
}

function saveActivityDraft(state, action) {
	const draft = {
		value: action.value,
		state: draftStateTypes.MODIFIED,
		isEditing: true,
	}
	return changeActivityDraftByActivityUid(state, action.activityUid, draft)
}

function setActivityDraftState(state, activityUid, newState) {
	const draft = state.drafts[activityUid]
	const newDraft = {
		value: draft.value,
		state: newState,
		isEditing: draft.isEditing,
	}
	return changeActivityDraftByActivityUid(state, activityUid, newDraft)
}

function enableActivityDraftForEdition(state, activityUid) {
	const draft = state.drafts[activityUid]
	const newDraft = {
		value: draft.value,
		state: draft.state,
		isEditing: true,
	}
	return changeActivityDraftByActivityUid(state, activityUid, newDraft)
}

function enableCurrentActivityDraft(state) {
	const currentActivityPayload = getActivityPayload(state.currentActivity)
	return enableActivityDraftForEdition(state, currentActivityPayload.uid)
}

function changeActivityDraftByActivityUid(state, activityUid, draft) {
	return {
		...state,
		drafts: {
			...state.drafts,
			[activityUid]: draft,
		},
	}
}

function updateActivitySubmissions(activity, participantUid, value) {
	const payloadKey = payloadTypes[activity.type]
	const payload = activity[payloadKey]
	// Create submission objects for activity types
	// and add to submissionValues
	function createOEQSubmission(value) {
		return { value }
	}
	function createMCQSubmission(value) {
		return { answerUids: value }
	}
	const submissionValues = {
		[activityTypes.MULTIPLE_CHOICE_QUESTION]: createMCQSubmission,
		[activityTypes.OPEN_ENDED_QUESTION]: createOEQSubmission,
	}
	const newPayload = {
		...payload,
		submissions:
		[
			...payload.submissions,
			{
				participantUid,
				...submissionValues[activity.type](value),
			},
		],
	}
	return {
		...activity,
		[payloadKey]: newPayload,
	}
}

function isCurrentActivity(currentActivity, activity) {
	const payloadKey = payloadTypes[activity.type]
	return currentActivity.type === activity.type &&
		currentActivity[payloadKey].uid === activity[payloadKey].uid
}

function updateActivity(activity, activities) {
	const payloadKey = payloadTypes[activity.type]
	const activityIndex = findActivityIndexByUid(
		activities,
		activity[payloadKey].uid,
	)
	return [
		...activities.slice(
			0,
			activityIndex
		),
		activity,
		...activities.slice(
			activityIndex + 1
		),
	]
}

function updateSubmission(state, questionUid, isAutosubmit = false) {
	const activity = getActivityByQuestionUid(state.activities, questionUid)
	const draft = state.drafts[questionUid]
	let newState = state
	const submitAllowedStates = [
		draftStateTypes.MODIFIED,
		draftStateTypes.ENABLED,
	]
	const autosubmitAllowedStates = [
		draftStateTypes.MODIFIED,
	]
	const allowedTypes = isAutosubmit ? autosubmitAllowedStates : submitAllowedStates
	const shouldAddSubmission = allowedTypes.includes(draft.state)
	if (shouldAddSubmission) {
		const updatedActivity = updateActivitySubmissions(
			activity,
			state.participantUid,
			draft.value,
		)
		if (state.sessionType !== sessionTypes.HOMEWORK && isCurrentActivity(state.currentActivity, updatedActivity)) {
			newState = {
				...newState,
				currentActivity: updatedActivity,
			}
		}
		newState = {
			...newState,
			activities: updateActivity(updatedActivity, newState.activities),
		}
		newState = setActivityDraftState(
			newState,
			questionUid,
			draftStateTypes.SUBMITTING,
		)
	}
	return newState
}

function isDraftValueEqualToSubmission(activity, draft, participantUid) {
	const compareSubmissionAndDraftValuesByType = {
		[activityTypes.OPEN_ENDED_QUESTION]: (submission, draft) =>
			submission.value === draft.value,
		[activityTypes.MULTIPLE_CHOICE_QUESTION]: (submission, draft) =>
			JSON.stringify(submission.answerUids.sort()) === JSON.stringify(draft.value.sort()),
	}
	const activityPayload = getActivityPayload(activity)
	const submission = activityPayload.submissions.find(submission => submission.participantUid === participantUid)
	if (submission && compareSubmissionAndDraftValuesByType[activity.type]) {
		return compareSubmissionAndDraftValuesByType[activity.type](submission, draft)
	}
	return false
}

function submitMcqAnswer(state, action) {
	// If the slide changed, state.drafts will be an empty object and we won't update the state
	if (!state.drafts || !state.drafts[action.questionUid]) {
		return state
	}
	const isAutosubmit = action.type === actionTypes.INTERACTIVE_VIDEO_MCQ_COMPONENT_UNMOUNTED

	const activity = findActivityByUid(state.activities, action.questionUid)
	// If the user didn't change the submission value, we set the draft back to not on edittion
	if (
		state.drafts[action.questionUid].isEditing &&
		isDraftValueEqualToSubmission(activity, state.drafts[action.questionUid], state.participantUid)
	) {
		state.drafts[action.questionUid].isEditing = false
	}

	const stateWithUpdatedSubmission = updateSubmission(state, action.questionUid, isAutosubmit)
	return {
		...stateWithUpdatedSubmission,
		activityState: activityStateTypes.SUBMITTED,
	}
}

function submitOeqAnswer(state, action) {
	// If the slide changed, state.drafts will be an empty object and we won't update the state
	if (!state.drafts || !state.drafts[action.questionUid]) {
		return state
	}
	const isAutosubmit = action.type === actionTypes.INTERACTIVE_VIDEO_OEQ_COMPONENT_UNMOUNTED

	const activity = findActivityByUid(state.activities, action.questionUid)
	// If the user didn't change the submission value, we set the draft back to not on edittion
	if (
		state.drafts[action.questionUid].isEditing &&
		isDraftValueEqualToSubmission(activity, state.drafts[action.questionUid], state.participantUid)
	) {
		state.drafts[action.questionUid].isEditing = false
	}

	const stateWithUpdatedSubmission = updateSubmission(state, action.questionUid, isAutosubmit)
	return {
		...stateWithUpdatedSubmission,
		activityState: activityStateTypes.SUBMITTED,
	}
}

function submitAnswerSuccess(state, action) {
	// If the slide changed, state.drafts will be an empty object and we won't update the state
	if (!state.drafts || !state.drafts[action.questionUid]) {
		return state
	}
	const draft = state.drafts[action.questionUid]
	if (draft.state === draftStateTypes.SUBMITTING) {
		const newDraft = {
			value: draft.value,
			state: draftStateTypes.SUBMITTED,
			isEditing: false,
		}
		return changeActivityDraftByActivityUid(state, action.questionUid, newDraft)
	}
	return state
}

function submitOeqAnswerFailed(state, action) {
	const oeqPayload = state.currentActivity.openEndedQuestionPayload
	if (oeqPayload && oeqPayload.uid === action.questionUid) {
		const submissionIndex = oeqPayload.submissions.findIndex(
			sub => sub.participantUid === state.participantUid
		)
		return {
			...state,
			currentActivity: {
				...state.currentActivity,
				openEndedQuestionPayload: {
					...oeqPayload,
					submissions:
						[
							...oeqPayload.submissions.slice(
								0,
								submissionIndex
							),
							...oeqPayload.submissions.slice(
								submissionIndex + 1
							),
						],
				},
			},
		}
	} else {
		return state
	}
}
function updateActivityState(state, activity) {
	const payloadKey = getActivityPayloadKey(activity)
	const activityIndex = findActivityIndexByUid(
		state.activities,
		activity[payloadKey].uid,
	)
	let currentActivity = state.currentActivity
	const currentActivityPayload = getActivityPayload(currentActivity)
	if (currentActivityPayload.uid === activity[payloadKey].uid) {
		currentActivity = {
			...state.currentActivity,
			[payloadKey]: {
				...activity[payloadKey],
			},
		}
	}
	return {
		...state,
		currentActivity,
		activities:
			[
				...state.activities.slice(
					0,
					activityIndex
				),
				activity,
				...state.activities.slice(
					activityIndex + 1
				),
			],
	}
}

function getCurrentActivityFromActivities(state) {
	const payload = getActivityPayload(state.currentActivity)
	return findActivityByUid(state.activities, payload.uid)
}

function addSharingOptionToActivity(state, newShareOption) {
	const activity = getCurrentActivityFromActivities(state)
	const activityPayload = getActivityPayload(activity)
	const newSharingOptions = addSharingOptionToSharingOptions(activityPayload.sharingOptions, newShareOption)
	return updateActivitySharingOptions(activity, newSharingOptions)
}

function removeSharingOptionFromActivity(state, shareOptionToRemove) {
	const activity = getCurrentActivityFromActivities(state)
	const activityPayload = getActivityPayload(activity)
	const newSharingOptions = removeSharingOptionFromSharingOptions(activityPayload.sharingOptions, shareOptionToRemove)
	return updateActivitySharingOptions(activity, newSharingOptions)
}

function shareMCQSolution(state) {
	const newShareMcqSolution = createShareSolution()
	const updatedActivity = addSharingOptionToActivity(state, newShareMcqSolution)
	return updateActivityState(state, updatedActivity)
}

function unshareMCQSolution(state) {
	const shareMcqSolutionToRemove = createShareSolution()
	const updatedActivity = removeSharingOptionFromActivity(state, shareMcqSolutionToRemove)
	return updateActivityState(state, updatedActivity)
}

function shareMCQSolutionFailed(state) {
	const newState = {
		...state,
		topInformation: [
			topInformationGenerator({
				message: localize('global_generic_error'),
				styleType: topInformationTypes.ERROR,
			}),
		],
	}
	return unshareMCQSolution(newState)
}

function unshareMCQSolutionFailed(state) {
	const newState = {
		...state,
		topInformation: [
			topInformationGenerator({
				message: localize('global_generic_error'),
				styleType: topInformationTypes.ERROR,
			}),
		],
	}
	return shareMCQSolution(newState)
}

function shareOEQParticipantSubmission(state, action) {
	const newShareParticipantSubmission = createShareParticipantSubmission(action.participantUid)
	const updatedActivity = addSharingOptionToActivity(state, newShareParticipantSubmission)
	return updateActivityState(state, updatedActivity)
}

function unshareOEQParticipantSubmission(state, action) {
	const shareParticipantSubmissionToRemove = createShareParticipantSubmission(action.participantUid)
	const updatedActivity = removeSharingOptionFromActivity(state, shareParticipantSubmissionToRemove)
	return updateActivityState(state, updatedActivity)
}

function shareOEQParticipantSubmissionFailed(state, action) {
	const newState = {
		...state,
		topInformation: [
			topInformationGenerator({
				message: localize('global_generic_error'),
				styleType: topInformationTypes.ERROR,
			}),
		],
	}
	return unshareOEQParticipantSubmission(newState, action)
}

function unshareOEQParticipantSubmissionFailed(state, action) {
	const newState = {
		...state,
		topInformation: [
			topInformationGenerator({
				message: localize('global_generic_error'),
				styleType: topInformationTypes.ERROR,
			}),
		],
	}
	return shareOEQParticipantSubmission(newState, action)
}

function handleToggleConfirmAccepted(state) {
	const newState = settingsInteractiveModeToggle(state)
	return closeConfirm(
		newState,
		confirmTypes.FEATURE_INTERACTIVE_VIDEO_IM_TOGGLE_CONFIRM_ID,
	)
}

function handleToggleConfirmRejected(state) {
	return closeConfirm(
		state,
		confirmTypes.FEATURE_INTERACTIVE_VIDEO_IM_TOGGLE_CONFIRM_ID,
	)
}

function handleVideoSessionInitialize(state, action) {
	const newState = initializeInteractiveVideo(state, action)
	return newState
}

function handleVideoPreviewInitialize(state, action) {
	//TODO: Remove after interactive-video-as-component FF is activated for all users
	return {
		...state,
		media: {
			...state.media,
			uid: action.uid,
		},
		role: userRoles.TEACHER,
		isActivityPanelExpanded: false,
		sessionType: sessionTypes.PREVIEW,
	}
}

function handleStandalonePreviewInitialize(state, action) {
	const decodedJwt = getDecodedJwt(action.jwt)
	const sessionType = parseInt(decodedJwt.extra.session_type)
	if (sessionType === sessionTypes.EMBED) {
		return {
			...state,
			externalUid: action.uid,
			role: userRoles.TEACHER,
			isActivityPanelExpanded: false,
			sessionType: sessionTypes.PREVIEW,
		}
	}
	return state
}

function openConfirm(state, confirm) {
	return {
		...state,
		confirms: [...state.confirms, confirm],
	}
}

function closeConfirm(state, confirmType) {
	return {
		...state,
		confirms:
			state.confirms.filter(confirm => confirm.type !== confirmType),
	}
}

function setInteractiveVideoLesson(state, action) {
	return {
		...state,
		lessonUid: action.entity.data.entityUID,
	}
}

function findActivityByUid(
	activities,
	activityUid,
) {
	return activities.find(
		activity => {
			const activityPayload = activity[payloadTypes[activity.type]]
			return activityPayload.uid === activityUid
		}
	)
}

function findBreakpointActivityByUid(
	breakpointsActivities,
	breakpointActivityUid
) {
	return breakpointsActivities.find(
		activity => activity.uid === breakpointActivityUid
	)
}

function getBreakpointsActivities(breakpoints) {
	return breakpoints.reduce(
		(activities, breakpoint) =>
			[...activities, ...breakpoint.activities], []
	)
}

function setInteractiveVideoNotifications(state, action) {
	if (action.payload.notificationMessage.type !== 1) {
		return openConfirm(state,
			{
				type: action.payload.notificationMessage.type,
				props: {},
			}
		)
	}
	return state
}

function updateHideStudentNames(state, action) {
	return {
		...state,
		settings: {
			...state.settings,
			shouldHideStudentNames: action.shouldHideStudentNames,
		},
	}
}

function updateStreamMode(state, action) {
	const newState = {
		...state,
		settings: {
			...state.settings,
			streamMode: action.streamMode,
		},
	}
	return closeConfirm(newState, confirmTypes.INTERACTIVE_VIDEO_STREAM_MODE_SELECT)
}

function setInteractiveVideoQuality(state, action) {
	return {
		...state,
		settings: {
			...state.settings,
			selectedVideoQuality: action.qualityId,
		},
	}
}

function setInteractiveVideoPlaybackSpeed(state, action) {
	return {
		...state,
		settings: {
			...state.settings,
			selectedVideoPlaybackSpeed: action.playbackSpeedId,
		},
	}
}

function closeTopInformationIfOnScope(state, uid) {
	return {
		...state,
		topInformation: closeTopInformation(state.topInformation, uid),
	}
}

function continuePostSessionReport(state) {
	return closeConfirm(
		state, confirmTypes.FEATURE_INTERACTIVE_VIDEO_POST_SESSION_INFORMATION_CONFIRM_ID,
	)
}

function submitCurrentActivity(state, action) {
	if (!action.canDispatchOnSlideChange) {
		return state
	}
	const activityTypesToSubmit = [
		activityTypes.OPEN_ENDED_QUESTION,
		activityTypes.MULTIPLE_CHOICE_QUESTION,
	]
	let activity = state.currentActivity
	if (state.sessionType === sessionTypes.HOMEWORK) {
		const participant = state.participants.find(participant => participant.uid === state.participantUid)
		if (!participant) {
			return state
		}
		const currentBreakpointActivityUid = state.participantState.currentBreakpointActivityUid
		activity = getCurrentActivityByBreakpointActivityUid(state, currentBreakpointActivityUid)
	}
	if (activityTypesToSubmit.includes(activity.type)) {
		const activityPayload = getActivityPayload(activity)
		const draft = state.drafts[activityPayload.uid]
		const shouldsubmit = draft.state === draftStateTypes.MODIFIED
		if (shouldsubmit) {
			return setActivityDraftState(state, activityPayload.uid, draftStateTypes.SUBMITTING)
		}
	}
	return state
}

function resetSession() {
	return defaultState
}

function blockParticipation(state) {
	return {
		...state,
		canParticipate: false,
	}
}

function handleVolumeSelected(state, action) {
	return {
		...state,
		settings: {
			...state.settings,
			volume: action.volume,
			isMuted: false,
		},
	}
}

function handleMuteSelected(state, action) {
	return {
		...state,
		settings: {
			...state.settings,
			isMuted: action.isMuted,
		},
	}
}

function submitBlankAnswerMcq(state, action) {
	const participant = state.participants.find(
		participant => participant.uid === state.participantUid
	)
	const isShowingPreventSkippingPrompt = shouldShowPreventSkippingPrompt(state, participant)
	const confirmType = isShowingPreventSkippingPrompt ?
		confirmTypes.INTERACTIVE_VIDEO_PREVENT_SKIPPING_PROMPT :
		confirmTypes.INTERACTIVE_VIDEO_BLANK_RESPONSE_PROMPT_MCQ

	return openConfirm(state,
		{
			type: confirmType,
			props: {
				questionUid: action.questionUid,
			},
		}
	)
}

function submitBlankAnswerOeq(state, action) {
	const participant = state.participants.find(
		participant => participant.uid === state.participantUid
	)
	const isShowingPreventSkippingPrompt = shouldShowPreventSkippingPrompt(state, participant)
	const confirmType = isShowingPreventSkippingPrompt ?
		confirmTypes.INTERACTIVE_VIDEO_PREVENT_SKIPPING_PROMPT :
		confirmTypes.INTERACTIVE_VIDEO_BLANK_RESPONSE_PROMPT_OEQ

	return openConfirm(state,
		{
			type: confirmType,
			props: {
				questionUid: action.questionUid,
			},
		}
	)
}

function mergeActivityWithCurrentActivity(currentActivity, activity) {
	const allowedActivityTypes = [
		activityTypes.OPEN_ENDED_QUESTION,
		activityTypes.POLL_QUESTION,
		activityTypes.MULTIPLE_CHOICE_QUESTION,
	]
	if (allowedActivityTypes.includes(currentActivity.type)) {
		const currentActivityPayload = getActivityPayload(currentActivity)
		if (currentActivityPayload.uid === activity.activityUid) {
			const payloadKey = payloadTypes[currentActivity.type]
			return {
				...currentActivity,
				[payloadKey]: {
					...currentActivityPayload,
					...activity,
					submissions: mergeActivitySubmissions(
						currentActivityPayload.submissions,
						activity.submissions,
					),
				},
			}
		}
	}

	return currentActivity
}

function initializeActivityDraft(activityData, drafts, activities, participantUid, activityState) {
	const activity = getActivityByQuestionUid(activities, activityData.activityUid)
	const activityPayload = getActivityPayload(activity)
	const submission = activityData.submissions.find(
		sub => sub.participantUid === participantUid
	)
	if (submission) {
		const oldDraft = drafts[activityPayload.uid]
		const shouldEnable = getFlag(featureFlagsTypes.INTERACTIVE_VIDEO_AUTOSUBMIT)
		&& activityState === activityStateTypes.STARTED
		const draftState = getNewDraftState(oldDraft, submission, activity.type)
		const value = getNewDraftValue(oldDraft, submission, activity.type)
		const newDraft = {
			state: draftState,
			value,
			isEditing: shouldEnable || oldDraft.isEditing,
		}
		return {
			...drafts,
			[activityPayload.uid]: newDraft,
		}
	} else {
		return drafts
	}
}

function handleActivityStatusFetched(state, action) {
	const newActivity = action.activity
	return {
		...state,
		activities: state.activities.map(activity => {
			const activityPayload = getActivityPayload(activity)
			if (activityPayload.uid === newActivity.activityUid) {
				const payloadKey = payloadTypes[activity.type]
				return {
					...activity,
					[payloadKey]: {
						...activityPayload,
						...newActivity,
						submissions: mergeActivitySubmissions(
							activityPayload.submissions,
							newActivity.submissions,
						),
					},
				}
			} else {
				return activity
			}
		}),
		currentActivity: mergeActivityWithCurrentActivity(state.currentActivity, newActivity),
		drafts: initializeActivityDraft(
			newActivity,
			state.drafts,
			state.activities,
			state.participantUid,
			state.activityState,
		),
	}
}

function handleActivityMounted(state) {
	return {
		...state,
		activityState: activityStateTypes.STARTED,
	}
}

function handleVideoCaptionsList(state, action) {
	return {
		...state,
		settings: {
			...state.settings,
			captions: action.captions,
		},
	}
}
function clearCaptions(state) {
	return {
		...state,
		settings: {
			...state.settings,
			selectedLanguage: defaultState.settings.selectedLanguage,
			captions: defaultState.settings.captions,
		},
	}
}

function handleCaptionsLanguageSelected(state, action) {
	const selectedCaption = state.settings.captions.find(caption => caption.srclang === action.language)
	const selectedLanguage = state.settings.selectedLanguage === selectedCaption.srclang ?
		'' : selectedCaption.srclang
	return {
		...state,
		settings: {
			...state.settings,
			selectedLanguage,
		},
	}
}

function shouldPreventSlideChange(state) {
	if (!getFlag(featureFlagsTypes.INTERACTIVE_VIDEO_PREVENT_SKIPPING_VIDEOS)
		|| [sessionTypes.EMBED, sessionTypes.PREVIEW].includes(state.sessionType)) {
		return false
	}
	// This will prevent all slide changes until the slide is initialized and the student is joined
	if (state.uid !== '' && (state.role === userRoles.TEACHER || state.participantUid !== '')) {
		if (state.participantUid !== ''
			&& state.isSubmissionRequiredEnabled
			&& state.sessionType === sessionTypes.HOMEWORK
			&& state.role === userRoles.STUDENT) {
			// This errorMargin is to be sure, since maximumTime could be up until 2 seconds lower than the video
			// duration from the maximumTime value updates (on interactiveVideo view componentDidUpdate)
			// plus one second because of Youtube duration difference
			const watchedTimeErrorMargin = 3100
			const participant = state.participants.find(
				participant => participant.uid === state.participantUid
			)
			const maximumTime = participant && state.participantState.maximumTimeReached || 0
			const trimmedDuration = state.media.trimEnd - state.media.trimStart
			const isVideoFullWatched = maximumTime >= trimmedDuration - watchedTimeErrorMargin
			if (isVideoFullWatched) {
				const hasSubmittedAllAnswers =
					!Object.entries(state.drafts)
						.map(([, value]) => value)
						.find(draft => draft.state === draftStateTypes.ENABLED)
				return !hasSubmittedAllAnswers
			}
		} else {
			return false
		}
	}
	return true
}

function handleSlideChanged(state, action) {
	let newState = state
	if (action.shouldEnforceSubmission && shouldPreventSlideChange(state)) {
		newState = {
			...newState,
			presentationSlideState: slideStateTypes.PREVENTING,
		}
	} else {
		newState = {
			...newState,
			presentationSlideState: slideStateTypes.CHANGING,
		}
		if (action.canDispatchOnSlideChange
			&& ![sessionTypes.EMBED, sessionTypes.PREVIEW].includes(state.sessionType)) {
			newState = submitCurrentActivity(newState, action)
		}
	}
	return newState
}

function resetPresentationSlideState(state) {
	const newState = {
		...state,
		presentationSlideState: slideStateTypes.STARTED,
	}
	return newState
}

function updateParticipantState(state, action) {
	return {
		...state,
		participantState: {
			...state.participantState,
			...action.participantState,
		},
	}
}

const interactiveVideoReducer = (state = defaultState, action) => {
	switch (action.type) {
		case actionTypes.INTERACTIVE_VIDEO_SESSION_INITIALIZE:
			return handleVideoSessionInitialize(state, action)
		case actionTypes.INTERACTIVE_VIDEO_PREVIEW_INITIALIZE:
			return handleVideoPreviewInitialize(state, action)
		case actionTypes.INTERACTIVE_VIDEO_STANDALONE_INITIALIZE:
			return handleStandalonePreviewInitialize(state, action)
		case actionTypes.INTERACTIVE_VIDEO_SESSION_STATUS_FETCHED:
		case actionTypes.INTERACTIVE_VIDEO_PREVIEW_FETCH_SUCCESS:
			return updateSessionData(state, action)
		case actionTypes.INTERACTIVE_VIDEO_PARTICIPANT_SUBMISSIONS_FETCHED:
			return initializeParticipantSubmissions(state, action)
		case actionTypes.INTERACTIVE_VIDEO_PLAY_SELECTED:
			return handlePlaySelected(state)
		case actionTypes.INTERACTIVE_VIDEO_SEEK_SELECTED:
			return setSlideState(state, videoStateTypes.VIDEO_STATE_SEEKING)
		case actionTypes.INTERACTIVE_VIDEO_PAUSE_SELECTED:
			return setSlideState(state, videoStateTypes.VIDEO_STATE_PAUSED)
		case actionTypes.INTERACTIVE_VIDEO_SEEK_ENDED:
			return handleSeekEnded(state)
		case actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_SELECTED:
			return handleBreakpointActivitySelected(state, action)
		case actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_CONFIRMED:
			return handleBreakpointActivityConfirmed(state, action)
		case actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_REACHED:
			return handleBreakpointActivityReached(state, action)
		case actionTypes.INTERACTIVE_VIDEO_BREAKPOINT_ACTIVITY_DISMISSED:
			return handleBreakpointActivityDismissed(state, action)
		case actionTypes.INTERACTIVE_VIDEO_ACTIVITY_PANEL_COLLAPSE_TOGGLE:
			return activityPanelCollapseToggle(state)
		case actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE:
			return settingsInteractiveModeToggle(state)
		case actionTypes.INTERACTIVE_VIDEO_MAX_TIME_REACHED:
			return setMaximumTimeReachedForParticipant(state, action)
		case actionTypes.INTERACTIVE_VIDEO_END_OF_VIDEO_REACHED:
			return handleEndOfVideoReached(state)
		case actionTypes.INTERACTIVE_VIDEO_PARTICIPANT_JOINED_SESSION:
			return participantJoinedSession(state, action)
		case actionTypes.INTERACTIVE_VIDEO_MCQ_SHARE_SOLUTION:
			return shareMCQSolution(state, action)
		case actionTypes.INTERACTIVE_VIDEO_MCQ_SHARE_SOLUTION_FAILED:
			return shareMCQSolutionFailed(state, action)
		case actionTypes.INTERACTIVE_VIDEO_MCQ_UNSHARE_SOLUTION:
			return unshareMCQSolution(state, action)
		case actionTypes.INTERACTIVE_VIDEO_MCQ_UNSHARE_SOLUTION_FAILED:
			return unshareMCQSolutionFailed(state, action)
		case actionTypes.INTERACTIVE_VIDEO_OEQ_SHARE_PARTICIPANT_SUBMISSION:
			return shareOEQParticipantSubmission(state, action)
		case actionTypes.INTERACTIVE_VIDEO_OEQ_SHARE_PARTICIPANT_SUBMISSION_FAILED:
			return shareOEQParticipantSubmissionFailed(state, action)
		case actionTypes.INTERACTIVE_VIDEO_OEQ_UNSHARE_PARTICIPANT_SUBMISSION:
			return unshareOEQParticipantSubmission(state, action)
		case actionTypes.INTERACTIVE_VIDEO_OEQ_UNSHARE_PARTICIPANT_SUBMISSION_FAILED:
			return unshareOEQParticipantSubmissionFailed(state, action)
		case actionTypes.CLOSE_TOP_INFORMATION:
			return closeTopInformationIfOnScope(state, action.uid)
		case actionTypes.INTERACTIVE_VIDEO_MCQ_COMPONENT_UNMOUNTED:
		case actionTypes.INTERACTIVE_VIDEO_SUBMIT_MCQ_ANSWER:
			return submitMcqAnswer(state, action)
		case actionTypes.INTERACTIVE_VIDEO_SUBMIT_MCQ_ANSWER_CANCELLED:
			return closeConfirm(
				state, confirmTypes.INTERACTIVE_VIDEO_SUBMIT_CONFIRM_MCQ,
			)
		case actionTypes.INTERACTIVE_VIDEO_SUBMIT_MCQ_ANSWER_SUCCESS:
		case actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER_SUCCESS:
			return submitAnswerSuccess(state, action)
		case actionTypes.INTERACTIVE_VIDEO_OEQ_COMPONENT_UNMOUNTED:
		case actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER:
			return submitOeqAnswer(state, action)
		case actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER_CANCELLED:
			return closeConfirm(
				state, confirmTypes.INTERACTIVE_VIDEO_SUBMIT_CONFIRM_OEQ,
			)
		case actionTypes.INTERACTIVE_VIDEO_SUBMIT_OEQ_ANSWER_FAILED:
			return submitOeqAnswerFailed(state, action)
		case actionTypes.INTERACTIVE_VIDEO_ENABLE_CURRENT_ACTIVITY_DRAFT:
			return enableCurrentActivityDraft(state)
		case actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE_CONFIRM_REJECTED:
			return handleToggleConfirmRejected(state)
		case actionTypes.INTERACTIVE_VIDEO_INTERACTIVE_MODE_TOGGLE_CONFIRM_ACCEPTED:
			return handleToggleConfirmAccepted(state)
		case actionTypes.INTERACTIVE_VIDEO_EXIT_SELECTED:
			return openConfirm(state,
				{
					type: confirmTypes.INTERACTIVE_VIDEO_EXIT_SELECTED,
					props: {},
				}
			)
		case actionTypes.INTERACTIVE_VIDEO_EXIT_SELECTED_CONFIRM_ACCEPTED:
		case actionTypes.INTERACTIVE_VIDEO_EXIT_SELECTED_CONFIRM_REJECTED:
			return closeConfirm(
				state, confirmTypes.INTERACTIVE_VIDEO_EXIT_SELECTED
			)
		case actionTypes.INTERACTIVE_VIDEO_LESSON_FETCHED:
			return setInteractiveVideoLesson(state, action)
		case actionTypes.INTERACTIVE_VIDEO_NOTIFICATIONS_FETCH_SUCCESS:
			return setInteractiveVideoNotifications(state, action)
		case actionTypes.INTERACTIVE_VIDEO_QUALITY_SELECTED:
			return setInteractiveVideoQuality(state, action)
		case actionTypes.SESSION_SETTINGS_HIDE_STUDENT_NAMES_UPDATE:
			return updateHideStudentNames(state, action)
		case actionTypes.SESSION_SETTINGS_STREAM_MODE_UPDATE:
			return updateStreamMode(state, action)
		case actionTypes.INTERACTIVE_VIDEO_PLAYBACK_SPEED_SELECTED:
			return setInteractiveVideoPlaybackSpeed(state, action)
		case actionTypes.INTERACTIVE_VIDEO_SAVE_ACTIVITY_DRAFT:
			return saveActivityDraft(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CONTINUE_POST_SESSION_REPORT:
			return continuePostSessionReport(state)
		case actionTypes.INTERACTIVE_VIDEO_SESSION_FINALIZE:
			return resetSession()
		case actionTypes.INTERACTIVE_VIDEO_SLIDE_CHANGED:
			return handleSlideChanged(state, action)
		case actionTypes.INTERACTIVE_VIDEO_PARTICIPANT_LIMIT_REACHED:
			return blockParticipation(state)
		case actionTypes.INTERACTIVE_VIDEO_VOLUME_SELECTED:
			return handleVolumeSelected(state, action)
		case actionTypes.INTERACTIVE_VIDEO_TOGGLE_MUTE_SELECTED:
			return handleMuteSelected(state, action)
		case actionTypes.INTERACTIVE_VIDEO_SUBMIT_BLANK_ANSWER_MCQ:
			return submitBlankAnswerMcq(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CLOSE_BLANK_RESPONSE_PROMPT_MCQ:
			return closeConfirm(
				state, confirmTypes.INTERACTIVE_VIDEO_BLANK_RESPONSE_PROMPT_MCQ
			)
		case actionTypes.INTERACTIVE_VIDEO_SUBMIT_BLANK_ANSWER_OEQ:
			return submitBlankAnswerOeq(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CLOSE_BLANK_RESPONSE_PROMPT_OEQ:
			return closeConfirm(
				state, confirmTypes.INTERACTIVE_VIDEO_BLANK_RESPONSE_PROMPT_OEQ
			)
		case actionTypes.INTERACTIVE_VIDEO_CLOSE_PREVENT_SKIPPING_PROMPT:
			return closeConfirm(state, confirmTypes.INTERACTIVE_VIDEO_PREVENT_SKIPPING_PROMPT)
		case actionTypes.INTERACTIVE_VIDEO_CLOSE_SUBMISSIONS_REQUIRED:
			return resetPresentationSlideState(state)
		case actionTypes.INTERACTIVE_VIDEO_CAPTIONS_FETCH_SUCCESS:
			return handleVideoCaptionsList(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CAPTIONS_LANGUAGE_SELECTED:
			return handleCaptionsLanguageSelected(state, action)
		case actionTypes.ACTIVITY_MCQ_STATUS_FETCHED:
		case actionTypes.ACTIVITY_OEQ_STATUS_FETCHED:
			return handleActivityStatusFetched(state, action)
		case actionTypes.ACTIVITY_MCQ_STUDENT_MOUNTED:
		case actionTypes.ACTIVITY_OEQ_STUDENT_MOUNTED:
			return handleActivityMounted(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CAPTIONS_FETCH_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_GOTO_PICKER:
			return clearCaptions(state)
		case actionTypes.INTERACTIVE_VIDEO_PARTICIPANT_STATE_FETCHED:
			return updateParticipantState(state, action)
		default: return state
	}
}

export default interactiveVideoReducer

export {
	defaultCurrentActivity,
	defaultState,
	findActivityIndexByUid,
	findActivityByUid,
	findBreakpointActivityByUid,
	getBreakpointsActivities,
	shouldPreventSlideChange,
}
